"use client";

import { Skeleton } from "antd";
import classNames from "classnames";
import Image, { ImageProps } from "next/image";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type SyntheticEvent,
} from "react";

export interface ProgressiveImageProps extends ImageProps {
  containerClassName?: string;
  skeletonClassName?: string;
  skeletonTestId?: string;
  disableSkeleton?: boolean;
  onReady?: () => void;
}

export function ProgressiveImage({
  containerClassName,
  skeletonClassName,
  skeletonTestId,
  disableSkeleton = false,
  onReady,
  onLoad,
  onError,
  className,
  ...imageProps
}: ProgressiveImageProps) {
  const [isReady, setIsReady] = useState(false);
  const { src, alt, ...restImageProps } = imageProps;
  const hasSrc = Boolean(src);
  const hasReportedReadyRef = useRef(false);
  const imageElementRef = useRef<HTMLImageElement | null>(null);

  const markReady = useCallback(() => {
    if (hasReportedReadyRef.current) {
      return;
    }
    hasReportedReadyRef.current = true;
    setIsReady(true);
    onReady?.();
  }, [onReady]);

  useEffect(() => {
    hasReportedReadyRef.current = false;

    if (!hasSrc) {
      markReady();
      return;
    }

    setIsReady(false);
    const imageElement = imageElementRef.current;
    if (
      imageElement?.complete &&
      imageElement.naturalWidth > 0 &&
      imageElement.naturalHeight > 0
    ) {
      markReady();
    }
  }, [hasSrc, markReady]);

  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      onLoad?.(event);
      markReady();
    },
    [onLoad, markReady]
  );

  const handleError = useCallback(
    (event: SyntheticEvent<HTMLImageElement, Event>) => {
      onError?.(event);
      markReady();
    },
    [onError, markReady]
  );

  return (
    <div className={classNames("relative", containerClassName)}>
      {!disableSkeleton && !isReady && (
        <div
          data-testid={skeletonTestId}
          className={classNames(
            "absolute inset-0 z-10 flex h-full w-full items-center justify-center bg-gray-200/80",
            skeletonClassName
          )}
        >
          <Skeleton.Node active className="h-full! w-full!" />
        </div>
      )}
      <Image
        {...restImageProps}
        src={src}
        alt={alt}
        ref={imageElementRef}
        className={classNames(
          "transition-opacity duration-300",
          {
            "opacity-0": !isReady && !disableSkeleton,
            "opacity-100": isReady || disableSkeleton,
          },
          className
        )}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
